export const FISHYDEX_CATALOG_VIEWS = Object.freeze(["simple", "grade", "guide"]);

// A protocol/presentation bound, not an estimate of browser storage capacity.
export const MAX_FISHYDEX_CATALOG_ITEMS = 4_096;

export const FISHYDEX_SURFACE_PROJECTION_FIELDS = Object.freeze([
  "search_query",
  "show_dried",
  "show_non_fish",
  "sort_field",
  "sort_direction",
  "catalog_view",
  "catalog_count",
  "fish_count",
  "visible_count",
  "caught_count",
  "completion_percent",
  "red_total_count",
  "red_caught_count",
  "red_completion_percent",
  "yellow_total_count",
  "yellow_caught_count",
  "yellow_completion_percent",
  "blue_total_count",
  "blue_caught_count",
  "blue_completion_percent",
  "green_total_count",
  "green_caught_count",
  "green_completion_percent",
  "white_total_count",
  "white_caught_count",
  "white_completion_percent",
  "supports_grade_filter",
  "supports_dried_filter",
  "supports_guide_view",
  "_catalog_state",
  "_loading",
  "_show_dried_pending",
  "_query_unavailable",
  "_api_error_message",
  "_api_error_hint",
]);

const FIELD_SET = new Set(FISHYDEX_SURFACE_PROJECTION_FIELDS);
const GRADE_ORDER = Object.freeze(["red", "yellow", "blue", "green", "white"]);
const BOOLEAN_FIELDS = new Set([
  "show_dried",
  "show_non_fish",
  "supports_grade_filter",
  "supports_dried_filter",
  "supports_guide_view",
  "_loading",
  "_show_dried_pending",
  "_query_unavailable",
]);
const COUNT_FIELDS = new Set([
  "catalog_count",
  "fish_count",
  "visible_count",
  "caught_count",
  ...GRADE_ORDER.flatMap((grade) => [`${grade}_total_count`, `${grade}_caught_count`]),
]);
const PERCENT_FIELDS = new Set([
  "completion_percent",
  ...GRADE_ORDER.map((grade) => `${grade}_completion_percent`),
]);
const TEXT_LIMITS = Object.freeze({
  search_query: 160,
  _api_error_message: 1_024,
  _api_error_hint: 1_024,
});

function boundedText(value, field) {
  const limit = TEXT_LIMITS[field];
  if (
    typeof value !== "string"
    || Array.from(value).length > limit
    || /[\u0000-\u001f\u007f]/.test(value)
  ) {
    throw new TypeError(`Fishydex projection ${field} must be bounded text`);
  }
}

function validateField(field, value) {
  if (BOOLEAN_FIELDS.has(field) && typeof value !== "boolean") {
    throw new TypeError(`Fishydex projection ${field} must be a boolean`);
  }
  if (
    COUNT_FIELDS.has(field)
    && (!Number.isSafeInteger(value) || value < 0 || value > MAX_FISHYDEX_CATALOG_ITEMS)
  ) {
    throw new TypeError(
      `Fishydex projection ${field} must be a bounded non-negative safe integer`,
    );
  }
  if (
    PERCENT_FIELDS.has(field)
    && (!Number.isSafeInteger(value) || value < 0 || value > 100)
  ) {
    throw new TypeError(`Fishydex projection ${field} must be an integer percent`);
  }
  if (Object.hasOwn(TEXT_LIMITS, field)) {
    boundedText(value, field);
  }
  if (field === "sort_field" && !["name", "price"].includes(value)) {
    throw new TypeError("Fishydex projection sort_field is unsupported");
  }
  if (field === "sort_direction" && !["asc", "desc"].includes(value)) {
    throw new TypeError("Fishydex projection sort_direction is unsupported");
  }
  if (field === "catalog_view" && !FISHYDEX_CATALOG_VIEWS.includes(value)) {
    throw new TypeError("Fishydex projection catalog_view is unsupported");
  }
  if (field === "_catalog_state" && !["pending", "ready", "unavailable"].includes(value)) {
    throw new TypeError("Fishydex projection _catalog_state is unsupported");
  }
}

function expectedPercent(caught, total) {
  return total === 0 ? 0 : Math.round((caught * 100) / total);
}

function validateCompleteProjection(value) {
  if (value.fish_count > value.catalog_count) {
    throw new TypeError("Fishydex fish count exceeds the projected catalog");
  }
  if (value.visible_count > value.catalog_count) {
    throw new TypeError("Fishydex visible count exceeds the projected catalog");
  }
  if (value.caught_count > value.fish_count) {
    throw new TypeError("Fishydex caught count exceeds the projected catalog");
  }
  if (value.completion_percent !== expectedPercent(value.caught_count, value.fish_count)) {
    throw new TypeError("Fishydex overall completion percent disagrees with its counts");
  }
  let gradeTotal = 0;
  let gradeCaught = 0;
  for (const grade of GRADE_ORDER) {
    const total = value[`${grade}_total_count`];
    const caught = value[`${grade}_caught_count`];
    const percent = value[`${grade}_completion_percent`];
    if (caught > total) {
      throw new TypeError(`Fishydex ${grade} caught count exceeds its total`);
    }
    if (percent !== expectedPercent(caught, total)) {
      throw new TypeError(`Fishydex ${grade} completion percent disagrees with its counts`);
    }
    gradeTotal += total;
    gradeCaught += caught;
  }
  if (gradeTotal > value.fish_count || gradeCaught > value.caught_count) {
    throw new TypeError("Fishydex grade progress exceeds the overall projection");
  }
}

function requireRecord(value, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  return value;
}

export function selectFishydexSurfaceProjection(value, { complete = false } = {}) {
  const source = requireRecord(value, "Fishydex presentation");
  const projection = {};
  for (const field of FISHYDEX_SURFACE_PROJECTION_FIELDS) {
    if (Object.hasOwn(source, field)) {
      validateField(field, source[field]);
      projection[field] = structuredClone(source[field]);
    } else if (complete) {
      throw new TypeError(`Fishydex initial projection is missing ${field}`);
    }
  }
  if (complete) {
    validateCompleteProjection(projection);
  }
  return projection;
}

export function applyFishydexSurfaceProjection(current, patch) {
  const source = requireRecord(patch, "Fishydex component projection");
  const unsupported = Object.keys(source).find((field) => !FIELD_SET.has(field));
  if (unsupported) {
    throw new TypeError(`Fishydex component projection has unsupported field ${unsupported}`);
  }
  const selected = selectFishydexSurfaceProjection(source);
  const next = current === null
    ? selectFishydexSurfaceProjection(source, { complete: true })
    : { ...structuredClone(current), ...selected };
  validateCompleteProjection(next);
  return structuredClone(next);
}
